import App from "../app";
import { PauseEvent, PingEvent, TickEvent } from "../events";
import { TimerExtras, TimeSpan } from "../lib/timer";
import { AttemptId, ItemId, ProductId, QuizId } from "../model/datasource";
import { ItemUsage } from "../model/datasource/item";
import { ListItem } from "../model/datasource/list";
import Controller from "./controller";

export class TimerController<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId, E extends TimerExtras> extends Controller<P, A, Z, I> {
	bind(target: EventTarget, app: App<P, A, Z, I>): void {
		target.addEventListener(TickEvent.type, () => this.tick(app));
		target.addEventListener(PingEvent.type, () => this.ping(app));
		target.addEventListener(PauseEvent.type, (e: PauseEvent) => this.pause(app, e.detail));
	}

	tick(app: App<P, A, Z, I>): void {
		const total: number = app.timer.total();
		app.view.header.displayTotalTime(total);
		if (app.timer.config.autoAdvance) {
			const now = new Date().getTime();
			app.view.header.displayCountdownTime(app.timer.config.advanceRate - (now - app.timer.lastAdvance));
		}
	}

	ping(app: App<P, A, Z, I>): void {
		Promise.all([
			app.model.metadata.getProductId(),
			app.model.metadata.getAttempt(),
			app.model.list.getItemIndexMap(),
			Promise.all(app.timer.recent()
				.map(({index, time}: { index: number; time: TimeSpan<E> }) =>
					app.content.meta(index)
						.then((item: ListItem<I>) => ({ item, time }))
				)
			)
		]).then((
			[product, attempt, indices, times]:
			[product: P, attempt: A, indices: string[], times: ItemUsage<I, E>[]]
		) => Promise.all([
			app.model.item.setPing(product, attempt, times),
			app.model.state.attempt.updateState({
				times: app.timer.serializeTimes(indices)
			})
		]));
	}

	pause(app: App<P, A, Z, I>, pause: boolean): void {
		if (pause) {
			app.timer.pause(app.current.get());
		} else {
			app.timer.unpause(app.current.get());
		}
		app.model.state.global.updateState({ timer: { timed: app.timer.config.timed } });
		app.view.item.displayPauseModal(app.getJquery(), pause);
		app.view.settings.displayTimed(!pause);
	}
}